import React, { useEffect, useState } from 'react';
import { Popup } from 'devextreme-react/popup';
import { Button, SelectBox, TextBox, NumberBox } from 'devextreme-react';
import { formatMessage } from 'devextreme/localization';

import FieldType from '../../consts/datasourcemodule/FieldType';

import TransformationTypeDto from '../../classes/dtos/pathcommonmodule/TransformationTypeDto';
import TransformationOptionsDto from '../../classes/dtos/transformationmodule/TransformationOptionsDto';

import './TransformationPopup.css';

interface TransformationPopupProps {
    visible: boolean;
    title: string;
    typeOptions: TransformationTypeDto[];
    currentType: number | null;
    currentParameters: string;
    getOptionsDetails: (type: number | null) => TransformationOptionsDto | null;
    getConstantFieldType?: (type: number | null) => number | null;
    onApply(type: number | null, parameters: string): void;
    onCancel(): void;
}

const TransformationPopup: React.FC<TransformationPopupProps> = ({
    visible, title, typeOptions, currentType, currentParameters,
    getOptionsDetails, getConstantFieldType, onApply, onCancel
}) => {

    const [type, setType] = useState<number | null>(currentType);
    const [parameters, setParameters] = useState<string>(currentParameters);

    useEffect(() => {
        if (visible) {
            setType(currentType);
            setParameters(currentParameters);
        }
    }, [visible, currentType, currentParameters]);

    const handleTypeChanged = (e: { value?: number | null }) => {
        const newType = e.value ?? null;
        setType(newType);
        setParameters(newType === currentType ? currentParameters : "");
    };

    const constantFieldType = (getConstantFieldType ? getConstantFieldType(type) : null);
    const options = constantFieldType === null ? getOptionsDetails(type) : null;

    let parsedOptionId: number | null = null;
    if (options && parameters) {
        try { parsedOptionId = JSON.parse(parameters).Id ?? null; } catch { /* not parsable yet */ }
    }

    let constantValue = "";
    if (constantFieldType !== null && parameters) {
        try { constantValue = JSON.parse(parameters).Value ?? ""; } catch { constantValue = parameters; }
    }

    // Un paramètre est obligatoire dès que le type choisi propose une liste d'options ou attend une
    // valeur constante : tant qu'il n'est pas renseigné, l'entrée reste incomplète et "Appliquer" est bloqué.
    const parameterRequired =
        (options !== null && (options.Options?.length ?? 0) > 0) || constantFieldType !== null;
    const parameterFilled = constantFieldType !== null
        ? constantValue.trim().length > 0
        : !!parsedOptionId;
    const parameterMissing = parameterRequired && !parameterFilled;
    const requiredError = { message: formatMessage("TransformationPopup_ParameterRequired") };

    const renderContent = (): JSX.Element => (
        <div className="transformation-popup-content">
            <div className="transformation-popup-field">
                <label>{formatMessage("TransformationPopup_Type")}</label>
                <SelectBox
                    dataSource={typeOptions}
                    displayExpr="Label"
                    valueExpr="Id"
                    value={type}
                    showClearButton={true}
                    stylingMode="outlined"
                    onValueChanged={handleTypeChanged}
                />
            </div>

            {options && (
                <div className="transformation-popup-field">
                    <label>{formatMessage("TransformationPopup_Parameters")}</label>
                    <SelectBox
                        dataSource={options.Options}
                        displayExpr="Label"
                        valueExpr="Id"
                        value={parsedOptionId}
                        stylingMode="outlined"
                        isValid={!parameterMissing}
                        validationError={requiredError}
                        onSelectionChanged={(e) => e.selectedItem && setParameters(JSON.stringify({ Id: e.selectedItem.Id }))}
                    />
                </div>
            )}

            {constantFieldType !== null && (
                <div className="transformation-popup-field">
                    <label>{formatMessage("TransformationPopup_Value")}</label>
                    {(constantFieldType === FieldType.Int32 || constantFieldType === FieldType.BigInt) ? (
                        <NumberBox
                            value={constantValue !== '' ? Number(constantValue) : undefined}
                            showSpinButtons={false}
                            format="#0"
                            stylingMode="outlined"
                            isValid={!parameterMissing}
                            validationError={requiredError}
                            onValueChanged={(e) => setParameters(JSON.stringify({ Value: e.value !== null && e.value !== undefined ? String(e.value) : '' }))}
                        />
                    ) : (
                        <TextBox
                            value={constantValue}
                            stylingMode="outlined"
                            isValid={!parameterMissing}
                            validationError={requiredError}
                            onValueChanged={(e) => setParameters(JSON.stringify({ Value: e.value }))}
                        />
                    )}
                </div>
            )}

            <div className="transformation-popup-actions">
                <Button
                    text={formatMessage("TransformationPopup_Cancel")}
                    onClick={onCancel}
                    stylingMode="outlined"
                    type="normal"
                />
                <Button
                    text={formatMessage("TransformationPopup_Apply")}
                    onClick={() => onApply(type, parameters)}
                    disabled={parameterMissing}
                    stylingMode="contained"
                    type="default"
                />
            </div>
        </div>
    );

    return (
        <Popup
            visible={visible}
            onHiding={onCancel}
            dragEnabled={false}
            hideOnOutsideClick={true}
            showCloseButton={true}
            showTitle={true}
            title={title}
            width={420}
            height="auto"
            // Build the content (and its DevExtreme editors) at mount rather than on first show,
            // otherwise the initial open stutters while the widgets are instantiated mid-animation
            // (same fix as FilterPopup.tsx).
            deferRendering={false}
            // The default popup "pop" animation (~300ms scale) reads as sluggish here — a short fade
            // (same fix as FilterPopup.tsx).
            animation={{
                show: { type: 'fade', from: 0, to: 1, duration: 90 },
                hide: { type: 'fade', from: 1, to: 0, duration: 60 },
            }}
            contentRender={renderContent}
        />
    );
};

export default TransformationPopup;
